import React, {useEffect, useState} from "react";
import {useCheckoutViewContext} from "../../CheckoutContextProvider/CheckoutViewContextProvider";
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {isAmazonLoggedIn} from "../../../lib/payments/AmazonPayLibrary";
import ShippingDatePicker from "./ShippingDatePicker";
import {ICartPosition} from "@connox/typescript-library";
import {IGetCheckoutShippingOptionsData} from "@connox/typescript-library/types/checkout/requestHandlers/CheckoutRequestHandler";
import ShippingOptionRow from "./ShippingOptionRow";
import styles from "./ShippingForm.module.scss";

export type IShippingFormData = {
  shippingOption: IGetCheckoutShippingOptionsData;
  phone?: string;
  shippingDate: string | null;
  isPartDeliveryAllowed: boolean;
};

const ShippingForm: React.FC<{
  phoneError: string; // unexpected phone error comes from server with 400 responce
  onChange: (data: IShippingFormData) => void;
}> = ({phoneError, onChange}) => {
  const {shippingOptions} = useCheckoutViewContext();
  const {checkout} = useCheckoutContext();
  const {t} = useTranslation("checkout");
  // phone error comes with address data
  const [addressPhoneError, setAddressPhoneError] = useState(phoneError);
  const [isPhoneRequired, setIsPhoneRequired] = useState(false);

  if (!shippingOptions.length) {
    return (
      <div className="Mobile-Only-Friendly">
        <p>{t("Es konnten keine Versandoptionen geladen werden.")}</p>
      </div>
    );
  }

  const defaultLogisticsType =
    shippingOptions.find((shipping) => shipping.logisticsTypeId === checkout.logisticsTypeId) ||
    shippingOptions.find((shipping) => !!shipping.isCustomerPreferredShipping) ||
    shippingOptions.find((shipping) => shipping.isBestMatch) ||
    shippingOptions[0];

  const {
    register,
    watch,
    setValue,
    formState: {errors},
  } = useForm<IShippingFormData>({
    mode: "onChange",
    defaultValues: {
      shippingOption: defaultLogisticsType,
      phone: checkout.deliveryAddress?.phone || "",
      shippingDate: checkout.favouredDateShipping,
      isPartDeliveryAllowed: checkout.isPartDeliveryAllowed,
    },
  });

  /*
  const showPartialDeliveryInput =
    checkout.cartData.positions.filter(
      (pos: ICartPosition) => pos.reference && pos.reference.itemId !== null && !!pos.item,
    ).length !== 1;
  */

  // Temporarily made invisible (FE-100503)
  const showPartialDeliveryInput = false;

  useEffect(() => {
    if (watch().shippingOption) {
      setIsPhoneRequired(
        shippingOptions.some(
          (shippingOption) =>
            Number(watch().shippingOption.logisticsTypeId) === shippingOption.logisticsTypeId &&
            shippingOption.isPhoneRequired,
        ) && !isAmazonLoggedIn(),
      );
    }
  }, [watch().shippingOption]);

  useEffect(() => {
    const messages = checkout.deliveryAddress?.messages || [];
    if (!phoneError) {
      setAddressPhoneError(messages?.find((msg) => msg.type === "error" && msg.field === "phone")?.code || "");
    } else setAddressPhoneError(phoneError);
  }, [checkout.deliveryAddress?.messages, phoneError]);

  useEffect(() => {
    onChange(watch());
  }, [watch().shippingDate]);

  return (
    <form
      className={styles.shippingForm}
      onChange={() => {
        onChange(watch());
      }}
    >
      <div className={`${styles.radioGroup} ArrowBorder`}>
        {shippingOptions.map((option) => {
          return (
            <ShippingOptionRow
              key={option.logisticsTypeId}
              option={option}
              selected={option.logisticsTypeId === watch().shippingOption.logisticsTypeId}
              onShippingChanged={(logisticsTypeId) => {
                setValue(
                  "shippingOption",
                  shippingOptions.find((optionToFind) => optionToFind.logisticsTypeId === logisticsTypeId),
                );
              }}
            />
          );
        })}
      </div>
      {isPhoneRequired && (
        <div
          className={styles.inputWrapper}
          onChange={() => {
            setAddressPhoneError("");
          }}
        >
          <input
            type={"tel"}
            placeholder={t("Telefonnummer für die Avisierung *")}
            {...register("phone", {required: isPhoneRequired, maxLength: 50})}
          />
          {addressPhoneError && <span>{t(addressPhoneError)}</span>}
          {errors.phone?.type === "required" && !addressPhoneError && <span>{t("Bitte ausfüllen")}</span>}
          <p className={styles.requiredMessage}>{t("* Pflichtfeld")}</p>
        </div>
      )}
      {showPartialDeliveryInput && (
        <div className={`${styles.inputWrapper} ArrowBorder`}>
          <label>
            <div>
              <input type={"checkbox"} {...register("isPartDeliveryAllowed")} />
            </div>
            <div>{t("Ich wünsche Teillieferungen ...")}</div>
          </label>
        </div>
      )}
      {/* Desired shipping date input */}

      <ShippingDatePicker
        deliveryDate={watch().shippingDate}
        onDeliveryDateChange={(date) => {
          setValue("shippingDate", date);
        }}
      />
    </form>
  );
};

export default ShippingForm;
